import 'reflect-metadata';
import { Inject, JsonPathReferenceHandler, Property, Type } from '@dipscope/type-manager';
import KNode from './KNode';
import { ActionDTO } from '@envclient/envcore';
import { MeepleDTO } from '../tilebase/Meeple';
import KMeeple from './KMeeple';
import { KTileDefClass } from './KTileGameConfig';
import { KPlayerResource, KResource } from './KResource';

export type Shape = 'hexa' | 'rhombus' | 'triangle';

// x:y:slot
export type KTilePos = `${number}:${number}:${number}`;

/**
 * Kposition is a triangle shaped position determined by the pivot point coords (x, y)
 * and a rotataton marker (slot 0-5 -> 0-300 deg rotation)
 * A KTile can occupie multiple positions: hexa-6, rhombus -2, trinagle - 1
 */

@Type({ discriminant: 'Number2', injectable: true })
export class Number2 extends Number {}

@Type({ discriminant: 'KPosition', injectable: true })
export class KPosition {
  @Property()
  x: number;
  @Property()
  y: number;
  @Property()
  slot: number; // slot position 0-5

  init(x: number, y: number, slot: number) {
    this.x = x || 0;
    this.y = y || 0;
    this.slot = slot || 0;
    return this;
  }

  toString(): KTilePos {
    return `${this.x.valueOf()}:${this.y.valueOf()}:${this.slot.valueOf()}`;
  }

  static fromString = (s: KTilePos): KPosition => {
    const p = s.split(':');
    const x = parseInt(p[0]);
    const y = parseInt(p[1]);
    const slot = parseInt(p[2]);
    return new KPosition().init(x, y, slot);
  };
}
@Type({
  discriminant: 'Place',
  injectable: true
})
export class Place {
  @Property()
  shape: Shape;
  @Property([KPosition])
  positions: KPosition[];

  constructor(shape: Shape = 'triangle', pos?: KPosition[]) {
    this.shape = shape;
    this.positions = pos || [];
  }
  toString() {
    this.positions.sort((a, b) => a.slot.valueOf() - b.slot.valueOf());
    return this.positions.map((p) => `${p.toString()}`).join('_');
  }
}

export type HexaPlaceType = 'centered' | 'south-east' | 'north-east';
export const hexaPlaceType = (place: Place): HexaPlaceType => {
  if (place.shape === 'hexa' && place.positions[0].x < place.positions[5].x) return 'north-east';
  if (place.shape === 'hexa' && place.positions[0].y > place.positions[1].y) return 'south-east';
  return 'centered';
};

const cloneAndOrderPlace = (place: Place) => {
  const p: Place = JSON.parse(JSON.stringify(place));
  p.positions.sort((a, b) => a.slot.valueOf() - b.slot.valueOf());
  return p;
};

// maps slot number to side index
export const hexaSlotToSideIndex = (placeOriginal: Place, slot: number): number => {
  const place = cloneAndOrderPlace(placeOriginal);
  const northEastMapping = {
    2: 0,
    5: 1,
    4: 2,
    1: 3,
    0: 4,
    3: 5
  };
  const southEastMapping = {
    4: 0,
    3: 1,
    0: 2,
    5: 3,
    2: 4,
    1: 5
  };
  // slot = sideIndex
  if (hexaPlaceType(place) === 'centered') {
    return slot;
  }
  if (hexaPlaceType(place) === 'north-east') {
    return northEastMapping[slot];
  }
  if (hexaPlaceType(place) === 'south-east') {
    return southEastMapping[slot];
  }
  return slot;
};

// maps triangle slot number to side index
export const triangleSlotToSideIndex = (place: Place, slot: number, externalSlot: number): number => {
  const triangleMapping = {
    0: {
      3: 0,
      1: 1,
      5: 2
    },
    1: {
      4: 0,
      2: 1,
      0: 2
    },
    2: {
      5: 0,
      3: 1,
      1: 2
    },
    3: {
      0: 0,
      4: 1,
      2: 2
    },
    4: {
      1: 0,
      5: 1,
      3: 2
    },
    5: {
      2: 0,
      0: 1,
      4: 2
    }
  };
  return triangleMapping[slot][externalSlot];
};

export const rhombusSlotToSideIndex = (originalPlace: Place, slot: number, externalSlot: number): number => {
  const place = cloneAndOrderPlace(originalPlace);
  const { x: x0, y: y0, slot: slot0 } = place.positions[0];
  const { x: x1, y: y1, slot: slot1 } = place.positions[1];
  // centered
  if (slot0 == 0 && slot1 == 1) {
    const mapping = {
      4: 0,
      2: 1,
      5: 2,
      3: 3
    };
    return mapping[externalSlot];
  }
  if (slot0 == 1 && slot1 == 2) {
    const mapping = {
      5: 0,
      3: 1,
      0: 2,
      4: 3
    };
    return mapping[externalSlot];
  }
  if (slot0 == 2 && slot1 == 3) {
    const mapping = {
      0: 0,
      4: 1,
      1: 2,
      5: 3
    };
    return mapping[externalSlot];
  }
  if (slot0 == 3 && slot1 == 4) {
    const mapping = {
      1: 0,
      5: 1,
      2: 2,
      0: 3
    };
    return mapping[externalSlot];
  }
  if (slot0 == 4 && slot1 == 5) {
    const mapping = {
      2: 0,
      0: 1,
      3: 2,
      1: 3
    };
    return mapping[externalSlot];
  }
  if (slot0 == 0 && slot1 == 5) {
    const mapping = {
      3: 0,
      1: 1,
      4: 2,
      2: 3
    };
    return mapping[externalSlot];
  }
  // overlapping  0,3 rotated like 4,5
  if (slot0 == 0 && slot1 == 3) {
    const mapping = {
      4: 0,
      2: 1,
      1: 2,
      5: 3
    };
    return mapping[externalSlot];
  }
  // overlapping - 1,4 rotated like 0,5
  if (slot0 == 1 && slot1 == 4) {
    const mapping = {
      5: 0,
      3: 1,
      2: 2,
      0: 3
    };
    return mapping[externalSlot];
  }
  // overlapping - 2,5 rotated like 0,1
  if (slot0 == 2 && slot1 == 5) {
    const mapping = {
      0: 0,
      4: 1,
      3: 2,
      1: 3
    };
    return mapping[externalSlot];
  }
};

export const toPlace = (p: string): Place => {
  const posParts = p.split('_');
  if (posParts.length === 1) {
    return new Place('triangle', [KPosition.fromString(posParts[0] as KTilePos)]);
  }
  if (posParts.length === 2) {
    return new Place('rhombus', [
      KPosition.fromString(posParts[0] as KTilePos),
      KPosition.fromString(posParts[1] as KTilePos)
    ]);
  }
  if (posParts.length === 6) {
    return new Place('hexa', [
      KPosition.fromString(posParts[0] as KTilePos),
      KPosition.fromString(posParts[1] as KTilePos),
      KPosition.fromString(posParts[2] as KTilePos),
      KPosition.fromString(posParts[3] as KTilePos),
      KPosition.fromString(posParts[4] as KTilePos),
      KPosition.fromString(posParts[5] as KTilePos)
    ]);
  }
};

@Type({
  discriminant: 'KTile',
  injectable: true
})
class KTile {
  @Property() id: number;
  @Property() type: number;
  @Property(Number) rot = 0;
  @Property() sides: string;
  @Property([KNode]) nodes: KNode[];
  @Property() meeple: KMeeple;
  @Property([KMeeple]) taxMeeples: KMeeple[] = [];
  @Property()
  shape: Shape;
  @Property()
  place: Place;
  @Property()
  isTemp: boolean = false;
  @Property() resource: KResource;
  @Property() playerResource?: KPlayerResource;
  @Property()
  mineCount: number = 0;

  constructor(obj?: KTileDefClass) {
    if (!obj) return;
    Object.keys(obj).forEach((key) => {
      this[key] = obj[key];
    });
  }

  toDTO(): KTileDTO {
    return {
      id: this.id,
      type: this.type,
      rot: this.rot,
      meeple: this.meeple ? this.meeple.toDTO() : null,
      taxMeeples: this.taxMeeples.map((m) => m.toDTO()),
      shape: this.shape,
      place: this.place,
      isTemp: this.isTemp,
      resource: this.resource,
      playerResource: this.playerResource,
      mineCount: this.mineCount
    };
  }
}

@Type({ injectable: true, discriminant: 'KTileDTO' })
export class KTileDTO {
  @Property()
  id: number;
  @Property()
  type: number;
  @Property()
  rot: number;
  @Property()
  meeple?: MeepleDTO;
  @Property([MeepleDTO])
  taxMeeples: MeepleDTO[];
  @Property()
  shape: Shape;
  @Property()
  place: Place;
  @Property()
  isTemp: boolean = false;
  @Property()
  resource?: KResource;
  @Property()
  playerResource?: KPlayerResource;
  @Property()
  mineCount?: number;
}

@Type({ injectable: true, discriminant: 'KActionDTO' })
export class KActionDTO implements ActionDTO {
  @Property()
  tile: KTileDTO;
}

@Type({ injectable: true, discriminant: 'KSubmitActionDTO' })
export class KSubmitActionDTO implements ActionDTO {
  @Property()
  tiles: KTileDTO[];
}

@Type({
  discriminant: 'ResourceMove',
  injectable: true
})
export class ResourceMove {
  @Property()
  resource: KResource;
  @Property([KNode])
  portNodes: KNode[];
  @Property()
  selectedNode?: KNode;
}

export type ResourceMoveDTO = {
  resource: KResource;
  targetTileId: number;
  targetNodeId: number;
};
@Type({ injectable: true, discriminant: 'KResourceMap' })
export class KResourceMap {
  @Property([Number, ResourceMove])
  map: Map<number, ResourceMove> = new Map();
}

@Type({ injectable: true, discriminant: 'KResourceActionDTO' })
export class KResourceActionDTO implements ActionDTO {
  @Property()
  resources: ResourceMoveDTO[];
}

@Type({
  discriminant: 'TaxPlacement',
  injectable: true
})
export class TaxPlacement {
  @Property()
  cityTileId: number;
  @Property([KNode])
  portNodes: KNode[];
  @Property()
  selectedNode?: KNode;

  constructor(cityTileId: number = 0) {
    this.cityTileId = cityTileId;
    this.portNodes = [];
  }
}
@Type({ injectable: true, discriminant: 'KTaxableNodeMap' })
export class KTaxableNodeMap {
  // map of cityTileId to array of taxable KNode-s
  @Property([Number, TaxPlacement])
  map: Map<number, TaxPlacement> = new Map();
}

export type TaxPlacementDTO = {
  tileId: number;
  nodeId: number;
  meepleId: number;
  playerId: number;
};

export class KTaxActionDTO implements ActionDTO {
  taxNodes: TaxPlacementDTO[];
}
export default KTile;
